import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "../App";
import { LogoMark } from "./Logo";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[14px] text-[#6B7280] hover:text-[#0A0A0A] mb-10 transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="flex items-center gap-2.5 mb-8">
            <LogoMark size={36} />
            <span className="font-bold text-[20px] tracking-[-0.02em] text-[#0A0A0A]">VeroBill</span>
          </div>

          <h1 className="font-display text-[32px] tracking-[-0.03em] text-[#0A0A0A] mb-2">
            Welcome back
          </h1>
          <p className="text-[15px] text-[#6B7280] mb-8">
            Sign in to continue to your dashboard
          </p>

          {error && (
            <div className="mb-6 p-4 bg-[#FEF2F2] border border-[#FECACA] text-[#DC2626] text-[14px]" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
                required
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-[13px] font-semibold text-[#0A0A0A] mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pr-12"
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#0A0A0A] transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-[13px] text-[#6B7280] hover:text-[#1A3DFF] transition-colors" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              data-testid="login-submit"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <p className="mt-8 text-center text-[14px] text-[#6B7280]">
            Don't have an account?{" "}
            <Link to="/register" className="font-semibold text-[#1A3DFF] hover:underline" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>

      {/* Right - Visual */}
      <div className="hidden lg:flex flex-1 bg-[#0A0A0A] items-center justify-center p-12">
        <div className="max-w-[400px] text-center">
          <h2 className="font-display text-[36px] tracking-[-0.03em] text-white mb-4">
            Invoicing.<br />Without the bullshit.
          </h2>
          <p className="text-[16px] text-[#9CA3AF]">
            Create and send professional invoices in seconds. Get paid faster.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
